import BeforeAfter from './BeforeAfter';
import SectionHead from './SectionHead';

/**
 * Showcase — the Vienna before/after, lifted out of the Hero.
 *
 * Why this section exists separately:
 *   The new Hero is the brand-guide "Website Header Example" treatment
 *   — clean, brand-forward, anchored by the luminous möbius mark.
 *   Stuffing a 16:9 interactive slider into that layout would have
 *   undone the calm composition the new hero is designed to deliver.
 *
 *   At the same time, the before/after is the single most persuasive
 *   moment on the entire page for this audience — a real screenshot
 *   pair from a real rebuild. Removing it would have been a sales
 *   mistake.
 *
 *   The compromise: pull it out into a dedicated section that sits
 *   directly under the Hero. The visitor still encounters the slider
 *   within the first scroll, the slider gets to breathe in its own
 *   container, and the hero stays clean.
 *
 * Copy choices:
 *   · Eyebrow "Recent rebuild" frames the slider as a real piece of
 *     work, not another stylized mock.
 *   · The section head leans into the verifiability — small business
 *     owners trust "you can click through and see the live site"
 *     more than any claim we could make.
 *   · The caption underneath the slider links to the live site.
 */
const viennaBefore = (
  <img
    className="site-shot"
    src="/brand/vienna-old-1200.jpg"
    srcSet="/brand/vienna-old-1200.jpg 1x, /brand/vienna-old-2400.jpg 2x"
    alt="Vienna Aquarium & Pets website, original version. Dated underwater-themed layout with fish-icon navigation rail and a 'Ready To Dive In? Click Here' callout."
    width={1200}
    height={1049}
    loading="lazy"
    decoding="async"
    draggable={false}
  />
);

const viennaAfter = (
  <img
    className="site-shot"
    src="/brand/vienna-new-1200.jpg"
    srcSet="/brand/vienna-new-1200.jpg 1x, /brand/vienna-new-2400.jpg 2x"
    alt="Vienna Aquarium & Pets website, rebuilt by WebNova. Modern layout with clean navigation, a 'Modern care for tanks, ponds, and pets.' headline, and a three-card 'What we help with' section."
    width={1200}
    height={1148}
    loading="lazy"
    decoding="async"
    draggable={false}
  />
);

export default function Showcase() {
  return (
    <section id="showcase" className="section showcase">
      <div className="wrap">
        <SectionHead
          eyebrow="Recent rebuild"
          title={
            <>
              Real before. <em className="serif">Real after.</em>
            </>
          }
          body="Drag the slider to reveal the rebuild. Every detail is on the live site — click through to see it for yourself."
        />

        <BeforeAfter before={viennaBefore} after={viennaAfter} />

        <p className="ba-caption">
          <span className="ba-caption-label">Live site</span>
          <span aria-hidden="true"> · </span>
          <a href="https://viennaaquariumandpets.com" target="_blank" rel="noopener noreferrer">
            viennaaquariumandpets.com <span aria-hidden="true">↗</span>
          </a>
        </p>
      </div>
    </section>
  );
}
